import React, { useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    User,
    CalendarDays,
    ArrowLeft,
    AlertCircle,
    BookOpen,
} from "lucide-react";
import Navbar from "../../../../components/navbar/Navbar";
import Footer from "../../../../components/footer/Footer";

// Shadow DOM Component to isolate HTML from Tailwind Preflight
// Shadow DOM Component to isolate HTML from Tailwind Preflight
const IsolatedHTML = ({ htmlContent }) => {
    const containerRef = useRef(null);

    useEffect(() => {
        if (containerRef.current) {
            if (!containerRef.current.shadowRoot) {
                containerRef.current.attachShadow({ mode: "open" });
            }

            containerRef.current.shadowRoot.innerHTML = `
                <style>
                    :host {
                        font-family: inherit;
                        color: #262626; /* text-neutral-800 */
                        line-height: 1.6; /* Tightened line height */
                        font-size: 1.125rem; /* Base text size (prose-lg equivalent) */
                    }
                    
                    /* 1. Force Strong and Bold to work */
                    strong, b {
                        font-weight: 700 !important;
                        color: #171717; /* Slightly darker for emphasis */
                    }
                    
                    /* 2. Control Heading Spacing and Weights */
                    h1, h2, h3, h4, h5, h6 {
                        color: #171717;
                        font-weight: 800 !important;
                        line-height: 1.3;
                        margin-top: 1.5em;
                        margin-bottom: 0.5em;
                    }
                    h3 { font-size: 1.5rem; }
                    
                    /* 3. Fix Paragraph Spacing and WYSIWYG Junk */
                    p {
                        margin-top: 0;
                        margin-bottom: 1rem;
                    }
                    /* Hides empty paragraphs or paragraphs with just a break */
                    p:empty { 
                        display: none; 
                    }
                    
                    /* 4. Restore List Styling and Spacing */
                    ul, ol {
                        margin-top: 0;
                        margin-bottom: 1.25rem;
                        padding-left: 1.5rem;
                    }
                    ul { list-style-type: disc; }
                    ol { list-style-type: decimal; }
                    li { margin-bottom: 0.35rem; }
                    li > ul, li > ol {
                        margin-top: 0.35rem;
                        margin-bottom: 0;
                    }
                    
                    /* 5. Image Safeguards */
                    img {
                        max-width: 100%;
                        height: auto;
                        border-radius: 0.75rem;
                        margin: 1.5rem 0;
                    }
                </style>
                ${htmlContent}
            `;
        }
    }, [htmlContent]);

    return <div ref={containerRef} />;
};

const BlogDetails = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const post = location.state;

    const handleImageError = (e) => {
        e.target.onerror = null;
        e.target.style.objectFit = "contain";
        e.target.style.backgroundColor = "#f0f0f0";
        e.target.style.filter = "grayscale(100%)";
        e.target.style.opacity = "0.5";
        e.target.src = "/assets/logo.png";
    };

    // Animation variants matching the established theme
    const fadeUpVariant = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: "easeOut" },
        },
    };

    if (!post) {
        return (
            <div className="bg-gray-50 min-h-screen flex flex-col">
                <main className="flex-grow pt-32 pb-24 px-4 flex items-center justify-center">
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={fadeUpVariant}
                        className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center max-w-lg w-full"
                    >
                        <div className="w-16 h-16 bg-red-50 border border-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                            <AlertCircle size={32} />
                        </div>
                        <h2 className="text-2xl font-black text-neutral-900 mb-4">
                            Blog Not Found
                        </h2>
                        <p className="text-gray-500 font-medium mb-8">
                            The article you are looking for does not exist or
                            has been removed.
                        </p>
                        <button
                            className="inline-flex items-center px-6 py-3 bg-neutral-900 hover:bg-yellow-400 hover:text-neutral-900 text-white font-bold rounded-lg transition-colors duration-300"
                            onClick={() => navigate(-1)}
                        >
                            <ArrowLeft size={18} className="mr-2" /> Go Back
                        </button>
                    </motion.div>
                </main>
            </div>
        );
    }

    return (
        <div className="bg-white min-h-screen flex flex-col">
            <main className="flex-grow pt-32 pb-24 px-4 sm:px-6 lg:px-8 w-full">
                <article className="container mx-auto">
                    {/* Back Navigation */}
                    <motion.button
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center text-sm font-bold text-gray-500 hover:text-neutral-900 transition-colors mb-10 group"
                    >
                        <ArrowLeft
                            size={16}
                            className="mr-2 transform group-hover:-translate-x-1 transition-transform"
                        />
                        Back to Articles
                    </motion.button>

                    {/* Article Header */}
                    <motion.header
                        initial="hidden"
                        animate="visible"
                        variants={fadeUpVariant}
                        className="mb-12"
                    >
                        <div className="flex items-center gap-2 text-red-700 font-bold text-xs uppercase mb-5 tracking-widest">
                            <span className="w-6 h-1 bg-red-700"></span>
                            <BookOpen size={14} className="text-red-700" />
                            Editorial Blog
                        </div>

                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 mb-8 leading-tight">
                            {post.title}
                        </h1>

                        {/* Meta Information */}
                        <div className="flex flex-wrap items-center gap-6 py-4 border-y border-gray-100">
                            {post.author && (
                                <div className="flex items-center text-neutral-900 font-bold text-sm uppercase tracking-widest">
                                    <User
                                        size={16}
                                        className="mr-2 text-red-700"
                                    />
                                    {post.author}
                                </div>
                            )}
                            <div className="flex items-center text-gray-500 font-bold text-sm uppercase tracking-widest">
                                <CalendarDays
                                    size={16}
                                    className="mr-2 text-yellow-500"
                                />
                                {post.created_at
                                    ? new Intl.DateTimeFormat("en-GB", {
                                          day: "numeric",
                                          month: "long",
                                          year: "numeric",
                                      }).format(new Date(post.created_at))
                                    : "N/A"}
                            </div>
                        </div>
                    </motion.header>

                    {/* Featured Image */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeUpVariant}
                        className="w-full max-w-3xl aspect-video rounded-2xl overflow-hidden bg-gray-100 mb-12 shadow-sm border border-gray-200"
                    >
                        <img
                            src={
                                Array.isArray(post.image)
                                    ? post.image[0]
                                    : post.image ||
                                      "/assets/placeholder-blog.jpg"
                            }
                            alt={post.title}
                            onError={handleImageError}
                            className="w-full h-full object-cover"
                        />
                    </motion.div>

                    {/* Main Content (Shadow DOM Isolated) */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeUpVariant}
                        className="w-full"
                    >
                        <IsolatedHTML
                            htmlContent={post.content || post.description}
                        />
                    </motion.div>
                </article>
            </main>
        </div>
    );
};

export default BlogDetails;
